import './variantsSelector.css';

import { useEffect, useMemo, useState } from 'react';
import { Attribute, useZakeke } from 'zakeke-configurator-react';

import {
  buttPlatesAttributesByCustomCategory,
  combsAttributesByCustomCategory,
  customCategoryNames,
  getPipeCodesByMM,
  platesBySport,
  rosettaSpheresAttributesByCustomCategory,
} from '../../data/data';
import {
  getAutomaticCombs,
  getCategoryForOption,
  isDefaultCategory,
} from '../../utils/configuratorLogic';
import { t } from '../../utils/translate';
import { useConfiguratorContext } from '../ConfiguratorWizard/ConfiguratorContext';
import Loader from '../Loader/Loader';

interface VariantsSelectorProps {
  onAttributesChange: (val: Attribute[]) => void;
}

export default function VariantsSelector({
  onAttributesChange,
}: VariantsSelectorProps) {
  // ==========================================================================
  // General
  // ==========================================================================
  const {
    isSceneLoading,
    groups,
    selectOption,
    templates,
    setTemplate,
    setCamera,
  } = useZakeke();

  const { sport, handle, pipeSize, lang } = useConfiguratorContext();

  // ==========================================================================
  // State
  // ==========================================================================
  const [selectedGroupId, selectGroup] = useState<number | null>(null);
  const [selectedStepId, selectStep] = useState<number | null>(null);
  const [selectedAttributeId, selectAttribute] = useState<number | null>(null);
  const [selectedCustomCategories, setSelectedCustomCategories] = useState<
    Record<string, string | null>
  >({
    Combs: 'NOCE',
    'Butt Plates': 'GOMMATI',
    'Rosetta/Spheres': 'STD',
  });
  const selectedGroup = groups.find((group) => group.id === selectedGroupId);
  const selectedStep = selectedGroup
    ? selectedGroup.steps.find((step) => step.id === selectedStepId)
    : null;

  // Attributes can be in both groups and steps, so show the attributes of step or in a group based on selection
  const attributes = useMemo(
    () => (selectedStep || selectedGroup)?.attributes ?? [],
    [selectedGroup, selectedStep],
  );

  const selectedAttribute = attributes.find(
    (attribute) => attribute.id === selectedAttributeId,
  );

  const filteredCombosOptions = useMemo(() => {
    if (
      selectedAttribute?.name === 'Combs' &&
      selectedCustomCategories['Combs']
    ) {
      const allowedCodes =
        combsAttributesByCustomCategory[selectedCustomCategories['Combs']];

      return selectedAttribute.options.filter((opt) =>
        allowedCodes.includes(JSON.parse(opt.code).id),
      );
    }
    return selectedAttribute?.options ?? [];
  }, [selectedAttribute, selectedCustomCategories['Combs']]);

  const filteredButtPlatesOptions = useMemo(() => {
    if (
      selectedAttribute?.name === 'Butt Plates' &&
      selectedCustomCategories['Butt Plates']
    ) {
      const allowedCodes =
        buttPlatesAttributesByCustomCategory[
          selectedCustomCategories['Butt Plates']
        ];

      return selectedAttribute.options.filter((opt) =>
        allowedCodes.includes(JSON.parse(opt.code).id),
      );
    }
    return selectedAttribute?.options ?? [];
  }, [selectedAttribute, selectedCustomCategories['Butt Plates']]);

  const filteredPlatesOptions = useMemo(() => {
    if (sport && platesBySport[sport]) {
      const allowedCodes = platesBySport[sport];

      const platesAttribute = attributes.find((attr) => attr.name === 'Plates');
      if (!platesAttribute) return;

      return platesAttribute.options.filter((opt) =>
        allowedCodes.includes(JSON.parse(opt.code).id),
      );
    }
    return selectedAttribute?.options ?? [];
  }, [selectedAttribute, sport]);

  const filteredPipesOptions = useMemo(() => {
    if (pipeSize) {
      const allowedCodes = getPipeCodesByMM(pipeSize);

      //const allowedCodes = pipesByPipeSize[pipeSize];

      const pipesAttribute = attributes.find((attr) => attr.name === 'Pipes');
      if (!pipesAttribute) return;

      return pipesAttribute.options.filter((opt) =>
        allowedCodes.includes(JSON.parse(opt.code).id),
      );
    }
    return selectedAttribute?.options ?? [];
  }, [selectedAttribute, pipeSize]);

  const filteredRosettaSpheresOptions = useMemo(() => {
    if (
      selectedAttribute?.name === 'Rosetta/Spheres' &&
      selectedCustomCategories['Rosetta/Spheres']
    ) {
      const allowedCodes =
        rosettaSpheresAttributesByCustomCategory[
          selectedCustomCategories['Rosetta/Spheres']
        ];

      return selectedAttribute.options.filter((opt) =>
        allowedCodes.includes(JSON.parse(opt.code).id),
      );
    }
    return selectedAttribute?.options ?? [];
  }, [selectedAttribute, selectedCustomCategories['Rosetta/Spheres']]);

  // Set camera
  useEffect(() => {
    if (selectedGroup) {
      const camera = selectedGroup.cameraLocationId;
      if (camera) setCamera(camera);
    }
  }, [selectedGroupId]);

  // Open the first group and the first step when loaded
  useEffect(() => {
    if (!selectedGroup && groups.length > 0) {
      selectGroup(groups[0].id);

      if (groups[0].steps.length > 0) selectStep(groups[0].steps[0].id);

      if (templates.length > 0) setTemplate(templates[0].id);
    }
  }, [selectedGroup, groups]);

  // Set handle option
  useEffect(() => {
    if (!handle || !attributes.length) return;

    const handlesAttribute = attributes.find((attr) => attr.name === 'Handles');
    if (!handlesAttribute) return;

    const match = handlesAttribute.options.find(
      (opt) => opt.name.toLowerCase() === handle.toLowerCase(),
    );

    if (match && !match.selected) {
      selectOption(match.id);
    }
  }, [attributes, handle]);

  // Select attribute first time and print in productSummary
  useEffect(() => {
    if (!selectedAttribute && attributes.length > 0) {
      selectAttribute(attributes[1].id);
    }

    onAttributesChange(attributes);
  }, [selectedAttribute, attributes]);

  // Select first valid option for Plates
  useEffect(() => {
    if (
      filteredPlatesOptions &&
      filteredPlatesOptions.length > 0 &&
      !filteredPlatesOptions.some((opt) => opt.selected)
    ) {
      selectOption(filteredPlatesOptions[0].id);
    }
  }, [selectedAttribute, filteredPlatesOptions]);

  // Select first valid option for Pipes
  useEffect(() => {
    if (
      filteredPipesOptions &&
      filteredPipesOptions.length > 0 &&
      !filteredPipesOptions.some((opt) => opt.selected)
    ) {
      selectOption(filteredPipesOptions[0].id);
    }
  }, [selectedAttribute, filteredPipesOptions]);

  // Reset correct category by option for Combs and Butt plates
  useEffect(() => {
    if (!selectedAttribute || !selectedAttribute.options) return;

    const activeOption = selectedAttribute.options.find((opt) => opt.selected);

    if (!activeOption) return;

    let activeOptionId = JSON.parse(activeOption.code).id;

    if (selectedAttribute.name === 'Butt Plates') {
      activeOptionId = activeOptionId.replace('_JPN', '');
    }

    const categoryMappings: Record<string, Record<string, string[]>> = {
      Combs: combsAttributesByCustomCategory,
      'Butt Plates': buttPlatesAttributesByCustomCategory,
      'Rosetta/Spheres': rosettaSpheresAttributesByCustomCategory,
    };

    if (selectedAttribute.name in categoryMappings) {
      const mappings = categoryMappings[selectedAttribute.name];
      const correctCategory = Object.entries(mappings).find(([, ids]) =>
        ids.includes(activeOptionId),
      )?.[0];

      if (
        correctCategory &&
        selectedCustomCategories[selectedAttribute.name] !== correctCategory
      ) {
        setSelectedCustomCategories((prev) => ({
          ...prev,
          [selectedAttribute.name]: correctCategory,
        }));
      }
    }
  }, [selectedAttribute]);

  // Preselezione automatica dei combs in base alla specialità e al tubo
  // Si attiva solo quando cambia sport o pipeSize, non quando l'utente cambia manualmente i combs
  useEffect(() => {
    if (!sport) return;

    const automaticCombs = getAutomaticCombs(sport, pipeSize);
    if (automaticCombs.length === 0) return;

    // Trova l'attributo Combs tra tutti i gruppi e step
    const combAttribute = groups
      .flatMap((group) => [
        ...group.attributes.filter((attr) => attr.name === 'Combs'),
        ...group.steps.flatMap((step) =>
          step.attributes.filter((attr) => attr.name === 'Combs'),
        ),
      ])
      .find((attr) => attr.name === 'Combs');

    if (!combAttribute || !combAttribute.options) return;

    // Cerca l'opzione che corrisponde ai combs automatici
    const matchingOption = combAttribute.options.find((option) => {
      try {
        const optionData = JSON.parse(option.code);
        return automaticCombs.includes(optionData.id);
      } catch {
        return false;
      }
    });

    // Se trova un'opzione corrispondente e non è già selezionata, la seleziona
    // Questo resetta solo quando cambia sport o pipe, non quando l'utente cambia manualmente i combs
    if (matchingOption && !matchingOption.selected) {
      selectOption(matchingOption.id);
    }
  }, [sport, pipeSize]);

  const handleSetWriteButtPlatesByRosettaSpheres = () => {
    const selectedCategory = selectedCustomCategories['Rosetta/Spheres'];

    const buttPlatesAttribute = attributes.find(
      (attr) => attr.name === 'Butt Plates',
    );

    if (!selectedCategory || !buttPlatesAttribute) return;

    const currentOption = buttPlatesAttribute.options.find(
      (opt) => opt.selected,
    );

    if (!currentOption) return;

    const currentId = JSON.parse(currentOption.code).id;

    let newId = '';
    if (selectedCategory === 'JPN') {
      newId = currentId.endsWith('_JPN') ? currentId : `${currentId}_JPN`;
    } else {
      newId = currentId.replace('_JPN', '');
    }

    const matchingOption = buttPlatesAttribute.options.find(
      (opt) => JSON.parse(opt.code).id === newId,
    );

    if (matchingOption && !matchingOption.selected) {
      selectOption(matchingOption.id);
    }
  };

  const handleSetCorrectIdButtPlatesByRosettaSpheres = (id: string) => {
    const selectedCategory = selectedCustomCategories['Rosetta/Spheres'];

    const buttPlatesAttribute = attributes.find(
      (attr) => attr.name === 'Butt Plates',
    );

    const rosettaSpheresAttribute = attributes.find(
      (attr) => attr.name === 'Rosetta/Spheres',
    );

    if (!selectedCategory || !buttPlatesAttribute || !rosettaSpheresAttribute)
      return;

    const currentrosettaSpheresOption = rosettaSpheresAttribute.options.find(
      (opt) => opt.selected,
    );

    const categoryForOption = getCategoryForOption(
      JSON.parse(currentrosettaSpheresOption!.code).id,
    );

    if (!categoryForOption) return;

    let newId = '';
    if (categoryForOption === 'JPN') {
      newId = id.endsWith('_JPN') ? id : `${id}_JPN`;
    } else {
      newId = id.replace('_JPN', '');
    }

    const matchingOption = buttPlatesAttribute.options.find(
      (opt) => JSON.parse(opt.code).id === newId,
    );

    if (matchingOption && !matchingOption.selected) {
      selectOption(matchingOption.id);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return isSceneLoading || !groups || groups.length === 0 ? (
    <Loader
      heightSection={'60rem'}
      position="center"
      label={t(lang, 'loader_label')}
    />
  ) : (
    <div className="variants-selector">
      <div className="tabs-header">
        {attributes &&
          attributes
            .filter((attribute) => attribute.name !== 'Handles')
            .map((attribute) => {
              return (
                <button
                  className={`tab-button ${
                    selectedAttribute === attribute ? 'active' : ''
                  }`}
                  key={attribute.id}
                  onClick={() => selectAttribute(attribute.id)}
                >
                  {attribute.name}
                </button>
              );
            })}
      </div>

      {/* CATEGORIES  COMBS*/}
      {selectedAttribute?.name === 'Combs' && (
        <>
          <p className="custom-category">Seleziona tipologia</p>
          <div className="tab-content-custom-category">
            {customCategoryNames['COMBS'].map((category) => (
              <button
                key={category}
                className={`selectable-button  ${
                  selectedCustomCategories['Combs'] === category ? 'active' : ''
                }`}
                onClick={() =>
                  setSelectedCustomCategories((prev) => ({
                    ...prev,
                    Combs: category,
                  }))
                }
              >
                {category}
              </button>
            ))}
          </div>
          <div className="tab-content">
            {filteredCombosOptions.map((option) => (
              <button
                className={`selectable-button ${option.selected ? 'active' : ''}`}
                key={option.id}
                onClick={() => selectOption(option.id)}
              >
                {option.name}
              </button>
            ))}
            {filteredCombosOptions.length === 0 && (
              <p className="not-found-message">Nessuna opzione disponibile</p>
            )}
          </div>
        </>
      )}

      {/* CATEGORIES  BUTT PLATES*/}
      {selectedAttribute?.name === 'Butt Plates' && (
        <>
          <p className="custom-category">Seleziona tipologia</p>
          <div className="tab-content-custom-category">
            {customCategoryNames['BUTT PLATES'].map((category) => (
              <button
                key={category}
                className={`selectable-button  ${
                  selectedCustomCategories['Butt Plates'] === category
                    ? 'active'
                    : ''
                }`}
                onClick={() =>
                  setSelectedCustomCategories((prev) => ({
                    ...prev,
                    'Butt Plates': category,
                  }))
                }
              >
                {category}
              </button>
            ))}
          </div>
          <div className="tab-content">
            {filteredButtPlatesOptions.map((option) => {
              const baseCode = JSON.parse(option.code).id;
              const jpnCode = `${baseCode}_JPN`;

              const isSelected =
                option.selected ||
                selectedAttribute?.options.some(
                  (opt) => JSON.parse(opt.code).id === jpnCode && opt.selected,
                );

              return (
                <button
                  className={`selectable-button ${isSelected ? 'active' : ''}`}
                  key={option.id}
                  onClick={() => {
                    handleSetCorrectIdButtPlatesByRosettaSpheres(
                      JSON.parse(option.code).id,
                    );
                  }}
                >
                  {option.name}
                </button>
              );
            })}
            {filteredButtPlatesOptions.length === 0 && (
              <p className="not-found-message">Nessuna opzione disponibile</p>
            )}
          </div>
        </>
      )}

      {/* CATEGORIES  PLATES*/}
      {selectedAttribute?.name === 'Plates' && (
        <div className="tab-content">
          {filteredPlatesOptions &&
            filteredPlatesOptions.map((option) => (
              <button
                className={`selectable-button ${option.selected ? 'active' : ''}`}
                key={option.id}
                onClick={() => selectOption(option.id)}
              >
                {option.name}
              </button>
            ))}
        </div>
      )}

      {/* CATEGORIES  PIPES*/}
      {selectedAttribute?.name === 'Pipes' && (
        <div className="tab-content">
          {filteredPipesOptions &&
            filteredPipesOptions.map((option) => (
              <button
                className={`selectable-button ${option.selected ? 'active' : ''}`}
                key={option.id}
                onClick={() => selectOption(option.id)}
              >
                {option.name}
              </button>
            ))}
        </div>
      )}

      {/* CATEGORIES  ROSETTA/SPHERES */}
      {selectedAttribute?.name === 'Rosetta/Spheres' && (
        <>
          <p className="custom-category">Seleziona tipologia</p>
          <div className="tab-content-custom-category">
            {customCategoryNames['Rosetta/Spheres'].map((category) => (
              <button
                key={category}
                className={`selectable-button  ${
                  selectedCustomCategories['Rosetta/Spheres'] === category
                    ? 'active'
                    : ''
                }`}
                onClick={() =>
                  setSelectedCustomCategories((prev) => ({
                    ...prev,
                    'Rosetta/Spheres': category,
                  }))
                }
              >
                {category}
              </button>
            ))}
          </div>
          <div className="tab-content">
            {filteredRosettaSpheresOptions.map((option) => (
              <button
                className={`selectable-button ${option.selected ? 'active' : ''}`}
                key={option.id}
                onClick={() => {
                  selectOption(option.id);
                  handleSetWriteButtPlatesByRosettaSpheres();
                }}
              >
                {option.name}
              </button>
            ))}
            {filteredRosettaSpheresOptions.length === 0 && (
              <p className="not-found-message">Nessuna opzione disponibile</p>
            )}
          </div>
        </>
      )}

      {/* ATTRIBUTES WITHOUT VARIATIONS  */}
      {isDefaultCategory(selectedAttribute?.name) && (
        <div className="tab-content">
          {selectedAttribute &&
            selectedAttribute.options.map((option) => (
              <button
                className={`selectable-button ${
                  option.selected ? 'active' : ''
                }`}
                key={option.id}
                onClick={() => selectOption(option.id)}
              >
                {option.name}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
